// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 zouwei

// Side-effect: registers the \ce/\pu chemistry macros (mhchem) on the katex
// instance BEFORE any render. Kept as a bare import so every consumer of this
// module gets chemistry support without extra wiring — same registration
// pattern as @moraya/core (schema.ts / plugins/math-node-views.ts).
import 'katex/contrib/mhchem'
import katex from 'katex'

/**
 * KaTeX strictness policy — mirrors @moraya/core's `katexStrict`
 * (katex-options.ts): CJK text inside math is legitimate teaching content
 * (`\ce{... ->[点燃] ...}`, `\xrightarrow{加热}`), and the default
 * strict:'warn' logs a console.warn PER CHARACTER PER RENDER. Downgrade that
 * one code to 'ignore'; everything else stays at 'warn'.
 */
export const katexStrict = (errorCode: string): 'ignore' | 'warn' =>
  errorCode === 'unicodeTextInMathMode' ? 'ignore' : 'warn'

export interface RenderFormulaOptions {
  /** Block math (true) vs inline math (false). Default true. */
  display?: boolean
}

/**
 * Render LaTeX (including mhchem chemistry) to a KaTeX HTML string.
 *
 * `throwOnError: false` — a half-typed formula renders as red source text
 * instead of throwing, which is the right behavior mid-edit on a canvas.
 * Output is KaTeX-generated markup from LaTeX source only (no raw user HTML
 * pass-through) and requires `katex/dist/katex.css` in the host page.
 */
export function renderFormulaHtml(
  latex: string,
  options: RenderFormulaOptions = {},
): string {
  return katex.renderToString(latex, {
    displayMode: options.display !== false,
    throwOnError: false,
    strict: katexStrict,
  })
}
